import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export type DialogSize = 'sm' | 'md' | 'lg';

type Props = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  titleId: string;
  descriptionId?: string;
  description?: ReactNode;
  size?: DialogSize;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  footer?: ReactNode;
  children: ReactNode;
  destructive?: boolean;
  /**
   * Optional classNames for the inner surface — escape hatch if the body needs
   * tighter/different padding (e.g., tables in CashSessionModal).
   */
  contentClassName?: string;
};

const sizeMap: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

const titleColorClass = (destructive?: boolean) =>
  destructive ? 'text-danger' : 'text-neutral-900';

export function Dialog({
  open,
  onClose,
  title,
  titleId,
  descriptionId,
  description,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
  initialFocusRef,
  footer,
  children,
  destructive = false,
  contentClassName,
}: Props) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Keep latest callbacks in refs so the open-lifecycle effect does NOT
  // re-run when parents pass a new onClose identity each render (e.g.
  // CheckoutModal's `onClose={() => ...}`). Re-running that effect
  // re-focuses the dialog surface and steals focus from inputs mid-type.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeOnEscRef = useRef(closeOnEsc);
  closeOnEscRef.current = closeOnEsc;
  const initialFocusRefStable = useRef(initialFocusRef);
  initialFocusRefStable.current = initialFocusRef;

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const html = document.documentElement;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    html.setAttribute('data-dialog-open', 'true');

    // Focus only on open. Prefer initialFocusRef after paint so the target
    // input exists in the tree (children may mount with the dialog).
    const focusRaf = requestAnimationFrame(() => {
      const target = initialFocusRefStable.current?.current ?? surfaceRef.current;
      target?.focus();
    });

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && closeOnEscRef.current) {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && surfaceRef.current) {
        const focusables = surfaceRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(focusRaf);
      document.removeEventListener('keydown', onKey);
      body.style.overflow = prevOverflow;
      html.removeAttribute('data-dialog-open');
      previouslyFocused.current?.focus?.();
    };
    // Intentionally only `open`: callback/ref identity must not re-run this.
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="backdrop fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && closeOnBackdrop) onClose();
      }}
    >
      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={`flex w-full ${sizeMap[size]} animate-fade-in bg-neutral-0 max-h-[90vh] flex-col rounded-lg text-neutral-900 shadow-[var(--shadow-ambient-high)] outline-none`}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 px-6 py-4">
          <h2 id={titleId} className={`text-lg font-semibold ${titleColorClass(destructive)}`}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar diálogo"
            className="flex h-11 w-11 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {description && (
          <div className="shrink-0 border-b border-neutral-200 px-6 py-3 text-sm text-neutral-600">
            {descriptionId ? <p id={descriptionId}>{description}</p> : <p>{description}</p>}
          </div>
        )}

        <div className={'min-h-0 flex-1 overflow-y-auto px-6 py-4 ' + (contentClassName ?? '')}>
          {children}
        </div>

        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-6 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
